/**
 * Eingegangene Terminanfragen.
 *
 * Hier stehen nur bestätigte Anfragen: unbestätigte sind für das Prüfbüro
 * unsichtbar, bis der Absender den Link in seiner E-Mail angeklickt hat.
 * Nichts trägt sich von selbst ein.
 */

import { useState } from 'react'
import {
  ZEITFENSTERTEXT,
  useAnfrageBeantworten,
  useTerminanfragen,
  type Terminanfrage,
} from '../api/aufgaben'
import { PRUEFARTTEXT } from '../api/typen'
import { Fehlerhinweis, Ladeanzeige, Leerhinweis, datumDeutsch } from '../bausteine/Hinweise'
import { darfPruefen, useKonto } from '../konto/KontoKontext'

export function Terminanfragen() {
  const { konto } = useKonto()
  const [alleZeigen, setzeAlleZeigen] = useState(false)
  const anfragen = useTerminanfragen(!alleZeigen)

  if (anfragen.isLoading) return <Ladeanzeige />
  if (anfragen.error) return <Fehlerhinweis fehler={anfragen.error} />

  const liste = anfragen.data ?? []

  return (
    <div className="space-y-4">
      <header>
        <h1 className="text-2xl font-bold">Terminanfragen</h1>
        <p className="text-slate-700">
          Über die öffentliche Seite eingegangen und vom Absender per E-Mail bestätigt.
        </p>
      </header>

      <label className="flex items-center gap-2 font-normal">
        <input
          type="checkbox"
          checked={alleZeigen}
          onChange={(e) => setzeAlleZeigen(e.target.checked)}
          style={{ width: '24px', height: '24px', minHeight: '24px' }}
        />
        <span className="text-base">Auch beantwortete zeigen</span>
      </label>

      {liste.length === 0 ? (
        <Leerhinweis
          titel={alleZeigen ? 'Noch keine Anfragen' : 'Nichts offen'}
          text="Unbestätigte Anfragen erscheinen hier nicht – erst nach dem Klick auf den Bestätigungslink."
        />
      ) : (
        <ul className="space-y-3">
          {liste.map((a) => (
            <AnfrageKarte key={a.id} anfrage={a} darfAntworten={darfPruefen(konto)} />
          ))}
        </ul>
      )}
    </div>
  )
}

function AnfrageKarte({
  anfrage,
  darfAntworten,
}: {
  anfrage: Terminanfrage
  darfAntworten: boolean
}) {
  const beantworten = useAnfrageBeantworten()
  const [formular, setzeFormular] = useState<'zusagen' | 'ablehnen' | null>(null)
  const [datum, setzeDatum] = useState(anfrage.wunschdatum ?? '')
  const [text, setzeText] = useState('')

  const offen = anfrage.status === 'bestaetigt'

  return (
    <li className="karte">
      <div className="flex flex-wrap items-start gap-3">
        <div className="min-w-0 flex-1">
          <div className="text-lg font-semibold">{anfrage.einrichtung}</div>
          <div className="text-base text-slate-700">
            {anfrage.ansprechpartner} ·{' '}
            <a href={`mailto:${anfrage.email}`} className="text-marke-700 underline">
              {anfrage.email}
            </a>
            {anfrage.telefon && (
              <>
                {' · '}
                <a href={`tel:${anfrage.telefon}`} className="text-marke-700 underline">
                  {anfrage.telefon}
                </a>
              </>
            )}
          </div>
          {(anfrage.strasse || anfrage.ort) && (
            <div className="text-base text-slate-700">
              {anfrage.strasse}
              {anfrage.strasse && (anfrage.plz || anfrage.ort) ? ', ' : ''}
              {anfrage.plz} {anfrage.ort}
            </div>
          )}
        </div>

        <span
          className={`marke ${
            anfrage.status === 'zugesagt'
              ? 'bg-stufe-ob-grund text-stufe-ob'
              : anfrage.status === 'abgelehnt'
                ? 'bg-stufe-na-grund text-stufe-na'
                : 'bg-marke-100 text-marke-700'
          }`}
        >
          {anfrage.status === 'bestaetigt'
            ? 'offen'
            : anfrage.status === 'zugesagt'
              ? `zugesagt ${datumDeutsch(anfrage.zugesagt_am)}`
              : 'abgelehnt'}
        </span>
      </div>

      <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1 text-base sm:grid-cols-4">
        <div>
          <dt className="text-slate-600">Wunschtermin</dt>
          <dd className="font-semibold">
            {datumDeutsch(anfrage.wunschdatum)}
            {anfrage.zeitfenster ? `, ${ZEITFENSTERTEXT[anfrage.zeitfenster]}` : ''}
          </dd>
        </div>
        <div>
          <dt className="text-slate-600">Art</dt>
          <dd className="font-semibold">
            {anfrage.pruefart ? PRUEFARTTEXT[anfrage.pruefart] : '—'}
          </dd>
        </div>
        <div>
          <dt className="text-slate-600">Geräte</dt>
          <dd className="font-semibold">{anfrage.anzahl_geraete ?? '—'}</dd>
        </div>
        <div>
          <dt className="text-slate-600">Eingegangen</dt>
          <dd className="font-semibold">{datumDeutsch(anfrage.erstellt_am.slice(0, 10))}</dd>
        </div>
      </dl>

      {anfrage.nachricht && (
        <p className="mt-3 whitespace-pre-wrap rounded-feld border-2 border-slate-200 bg-slate-50 p-3 text-base">
          {anfrage.nachricht}
        </p>
      )}

      {anfrage.antwort && (
        <p className="mt-3 text-base text-slate-700">
          <span className="font-semibold">Antwort:</span> {anfrage.antwort}
        </p>
      )}

      {offen && darfAntworten && (
        <div className="mt-3">
          {formular === null ? (
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                className="knopf-haupt"
                onClick={() => setzeFormular('zusagen')}
              >
                Termin zusagen
              </button>
              <button
                type="button"
                className="knopf-still"
                onClick={() => setzeFormular('ablehnen')}
              >
                Ablehnen
              </button>
            </div>
          ) : (
            <form
              className="space-y-3"
              onSubmit={(e) => {
                e.preventDefault()
                beantworten.mutate({
                  id: anfrage.id,
                  art: formular,
                  daten: {
                    zugesagt_am: formular === 'zusagen' ? datum : null,
                    antwort: text.trim() || null,
                  },
                })
              }}
            >
              {formular === 'zusagen' && (
                <div>
                  <label htmlFor={`datum-${anfrage.id}`}>Termin</label>
                  <input
                    id={`datum-${anfrage.id}`}
                    type="date"
                    value={datum}
                    onChange={(e) => setzeDatum(e.target.value)}
                    required
                  />
                </div>
              )}
              <div>
                <label htmlFor={`text-${anfrage.id}`}>
                  Nachricht an {anfrage.ansprechpartner}
                </label>
                <textarea
                  id={`text-${anfrage.id}`}
                  value={text}
                  onChange={(e) => setzeText(e.target.value)}
                  placeholder={
                    formular === 'zusagen'
                      ? 'Wir kommen vormittags, bitte den Bauhof informieren.'
                      : 'Im Oktober ausgebucht, bitte im Frühjahr erneut anfragen.'
                  }
                  style={{ minHeight: '72px' }}
                />
              </div>

              <Fehlerhinweis fehler={beantworten.error} />

              <div className="flex gap-2">
                <button
                  type="submit"
                  className="knopf-haupt"
                  disabled={beantworten.isPending}
                >
                  {beantworten.isPending
                    ? 'Sendet …'
                    : formular === 'zusagen'
                      ? 'Zusagen und E-Mail schicken'
                      : 'Ablehnen und E-Mail schicken'}
                </button>
                <button
                  type="button"
                  className="knopf-still"
                  onClick={() => setzeFormular(null)}
                >
                  Abbrechen
                </button>
              </div>
            </form>
          )}
        </div>
      )}
    </li>
  )
}
